import React, { useState } from 'react';
import {
  Card,
  List,
  Button,
  Modal,
  Input,
  InputNumber,
  Space,
  Typography,
  Tooltip,
  Empty,
  message,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { SubtitleEntry } from '../types';

const { Text } = Typography;
const { TextArea } = Input;

interface SubtitleEditorPanelProps {
  subtitleEntries: SubtitleEntry[];
  currentTime: number;
  onSeek: (time: number) => void;
  onUpdateEntry: (index: number, entry: SubtitleEntry) => void;
  onDeleteEntry: (index: number) => void;
  onAddEntry: (entry: SubtitleEntry) => void;
  language: string;
}

interface EditingEntry {
  index: number;
  entry: SubtitleEntry;
}

export const SubtitleEditorPanel: React.FC<SubtitleEditorPanelProps> = ({
  subtitleEntries,
  currentTime,
  onSeek,
  onUpdateEntry,
  onDeleteEntry,
  onAddEntry,
  language,
}) => {
  const [editingEntry, setEditingEntry] = useState<EditingEntry | null>(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newEntry, setNewEntry] = useState<SubtitleEntry>({
    text: '',
    startTime: 0,
    endTime: 1,
    confidence: 1,
  });

  // Format time as MM:SS.mmm
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };

  // Parse time string to seconds
  const parseTime = (timeStr: string): number => {
    const parts = timeStr.split(':');
    if (parts.length !== 2) return 0;
    
    const mins = parseInt(parts[0], 10) || 0;
    const secParts = parts[1].split('.');
    const secs = parseInt(secParts[0], 10) || 0;
    const ms = parseInt(secParts[1], 10) || 0;
    
    return mins * 60 + secs + ms / 1000;
  };

  const handleEdit = (index: number, entry: SubtitleEntry) => {
    setEditingEntry({ index, entry: { ...entry } });
  };

  const handleSaveEdit = () => {
    if (!editingEntry) return;
    
    const { index, entry } = editingEntry;
    
    // Validation
    if (!entry.text.trim()) {
      message.error('Subtitle text cannot be empty');
      return;
    }
    
    if (entry.startTime >= entry.endTime) {
      message.error('Start time must be before end time');
      return;
    }
    
    onUpdateEntry(index, entry);
    setEditingEntry(null);
    message.success('Subtitle updated');
  };

  const handleDelete = (index: number) => {
    onDeleteEntry(index);
    message.success('Subtitle deleted');
  };

  const handleAdd = () => {
    // Validation
    if (!newEntry.text.trim()) {
      message.error('Subtitle text cannot be empty');
      return;
    }
    
    if (newEntry.startTime >= newEntry.endTime) {
      message.error('Start time must be before end time');
      return;
    }
    
    onAddEntry(newEntry);
    setIsAddModalVisible(false);
    setNewEntry({
      text: '',
      startTime: currentTime,
      endTime: currentTime + 2,
      confidence: 1,
    });
    message.success('Subtitle added');
  };

  const openAddModal = () => {
    setNewEntry({
      text: '',
      startTime: currentTime,
      endTime: currentTime + 2,
      confidence: 1,
    });
    setIsAddModalVisible(true);
  };

  // Find current active subtitle
  const activeIndex = subtitleEntries.findIndex(
    (entry) => currentTime >= entry.startTime && currentTime <= entry.endTime
  );

  return (
    <Card
      title={
        <Space>
          <span>📝 Subtitles ({language.toUpperCase()})</span>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {subtitleEntries.length} entries
          </Text>
        </Space>
      }
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={openAddModal}
        >
          Add
        </Button>
      }
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      bodyStyle={{ flex: 1, overflow: 'auto', padding: '8px' }}
    >
      {subtitleEntries.length === 0 ? (
        <Empty
          description="No subtitles yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          dataSource={subtitleEntries}
          renderItem={(entry, index) => {
            const isActive = index === activeIndex;
            
            return (
              <List.Item
                key={index}
                style={{
                  padding: '8px',
                  marginBottom: '4px',
                  backgroundColor: isActive ? '#e6f7ff' : '#fafafa',
                  border: isActive ? '1px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => onSeek(entry.startTime)}
              >
                <div style={{ width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{ fontSize: '11px', fontFamily: 'monospace' }}
                    >
                      ⏱ {formatTime(entry.startTime)} → {formatTime(entry.endTime)}
                    </Text>
                    <Space size="small">
                      <Tooltip title="Edit">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(index, entry);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(index);
                          }}
                        />
                      </Tooltip>
                    </Space>
                  </div>
                  <Text style={{ fontSize: '13px', lineHeight: '1.4' }}>
                    {entry.text}
                  </Text>
                </div>
              </List.Item>
            );
          }}
        />
      )}

      {/* Edit Modal */}
      <Modal
        title="Edit Subtitle"
        open={!!editingEntry}
        onOk={handleSaveEdit}
        onCancel={() => setEditingEntry(null)}
        okText="Save"
        width={500}
      >
        {editingEntry && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Start Time (MM:SS.mmm)</Text>
              <Input
                value={formatTime(editingEntry.entry.startTime)}
                onChange={(e) => {
                  const time = parseTime(e.target.value);
                  setEditingEntry({
                    ...editingEntry,
                    entry: { ...editingEntry.entry, startTime: time },
                  });
                }}
                placeholder="00:00.000"
              />
            </div>
            <div>
              <Text strong>End Time (MM:SS.mmm)</Text>
              <Input
                value={formatTime(editingEntry.entry.endTime)}
                onChange={(e) => {
                  const time = parseTime(e.target.value);
                  setEditingEntry({
                    ...editingEntry,
                    entry: { ...editingEntry.entry, endTime: time },
                  });
                }}
                placeholder="00:03.500"
              />
            </div>
            <div>
              <Text strong>Subtitle Text</Text>
              <TextArea
                value={editingEntry.entry.text}
                onChange={(e) =>
                  setEditingEntry({
                    ...editingEntry,
                    entry: { ...editingEntry.entry, text: e.target.value },
                  })
                }
                rows={4}
                placeholder="Enter subtitle text..."
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* Add Modal */}
      <Modal
        title="Add New Subtitle"
        open={isAddModalVisible}
        onOk={handleAdd}
        onCancel={() => setIsAddModalVisible(false)}
        okText="Add"
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Start Time (MM:SS.mmm)</Text>
            <Input
              value={formatTime(newEntry.startTime)}
              onChange={(e) => {
                const time = parseTime(e.target.value);
                setNewEntry({ ...newEntry, startTime: time });
              }}
              placeholder="00:00.000"
            />
          </div>
          <div>
            <Text strong>End Time (MM:SS.mmm)</Text>
            <Input
              value={formatTime(newEntry.endTime)}
              onChange={(e) => {
                const time = parseTime(e.target.value);
                setNewEntry({ ...newEntry, endTime: time });
              }}
              placeholder="00:03.500"
            />
          </div>
          <div>
            <Text strong>Subtitle Text</Text>
            <TextArea
              value={newEntry.text}
              onChange={(e) => setNewEntry({ ...newEntry, text: e.target.value })}
              rows={4}
              placeholder="Enter subtitle text..."
            />
          </div>
        </Space>
      </Modal>
    </Card>
  );
};
